/**
 * About Page
 * Noodle Studio
 *
 * Colour follows the design system (docs/DESIGN_SYSTEM.md): a feature that is a kind of node
 * wears that kind's tone (Image lavender, Video flame, Script lemon, Post and Storage ink);
 * everything else takes the brand's lavender mark.
 */

import React from 'react';
import { BetaTag, BrandMark } from '../components/BrandMark';
import {
  ArrowLeft, ArrowRight, Sparkles, Palette, Video, Share2, Cloud, Shield, Code, FileText, Heart, Zap, Globe, Users,
  type LucideIcon,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { usePageMeta, PAGE_META } from '../hooks/usePageMeta';
import type { NodeTone } from '../components/nodes/nodeTheme';

interface AboutPageProps {
  onBack: () => void;
  onTerms: () => void;
  onPrivacy: () => void;
}

/** What the studio does. `tone` is the tone of the node kind the feature is; without one the mark is the brand's. */
const FEATURES: { icon: LucideIcon; tone?: NodeTone; title: string; body: string }[] = [
  {
    icon: Palette,
    tone: 'lavender',
    title: 'AI image generation',
    body: 'Create stunning images from text prompts using state-of-the-art AI models. Support for multiple providers including OpenAI DALL-E, Google Gemini, and more.',
  },
  {
    icon: Video,
    tone: 'flame',
    title: 'Video generation',
    body: 'Transform still images into dynamic videos. Create animations, transitions, and short-form video content powered by cutting-edge AI.',
  },
  {
    icon: FileText,
    tone: 'lemon',
    title: 'AI writing and scripts',
    body: 'Write stories, scripts, and creative content with AI assistance. From short poems to full storyboards, let AI help you craft compelling narratives.',
  },
  {
    icon: Share2,
    tone: 'ink',
    title: 'Social sharing',
    body: 'Share your creations directly to Twitter/X with one click. Schedule posts, add captions, and grow your audience with AI-powered content.',
  },
  {
    icon: Cloud,
    tone: 'ink',
    title: 'Cloud storage',
    body: 'Your projects are automatically saved to the cloud. Access your work from anywhere, never lose progress, and keep your creative journey organized.',
  },
  {
    icon: Shield,
    title: 'Secure and private',
    body: 'Your API keys are encrypted server-side. Your creations belong to you. We prioritize your privacy and security at every step.',
  },
];

const TECH: { title: string; items: string[] }[] = [
  { title: 'Frontend', items: ['React 18 with TypeScript', 'Tailwind CSS for styling', 'Vite for fast development', 'React Flow for infinite canvas'] },
  { title: 'Backend and infrastructure', items: ['Firebase Authentication', 'Firebase Firestore & Cloud Storage for data storage', 'Netlify Functions (serverless)', 'Cloudflare CDN'] },
  { title: 'AI integrations', items: ['OpenAI (GPT, DALL-E)', 'Google AI (Gemini)', 'ComfyUI support'] },
  { title: 'Payments', items: ['Stripe for subscriptions', 'Solana for crypto payments', 'Secure payment processing'] },
];

const VALUES: { icon: LucideIcon; title: string; body: string }[] = [
  { icon: Sparkles, title: 'Creativity first', body: 'Every feature is designed to enhance, not replace, human creativity.' },
  { icon: Users, title: 'User-centric', body: 'We listen to our community and build what creators actually need.' },
  { icon: Globe, title: 'Accessible', body: 'Professional-grade AI tools should be available to everyone.' },
];

export const AboutPage: React.FC<AboutPageProps> = ({ onBack, onTerms, onPrivacy }) => {
  usePageMeta(PAGE_META.about);

  return (
    <div className="fixed inset-0 z-[200] bg-cream overflow-auto">
      {/* The bar every page has: the way back, and whose page this is. The page's name heads the content. */}
      <header className="ns-pagebar">
        <div className="w-full max-w-4xl mx-auto flex items-center justify-between gap-3">
          <button onClick={onBack} className="ns-btn ns-btn-ghost ns-btn-sm shrink-0">
            <ArrowLeft size={14} aria-hidden="true" />
            Back
          </button>
          <Link to="/" className="flex items-center gap-2.5 rounded-full shrink-0" aria-label="Noodle Studio home">
            <BrandMark size={32} />
            <span className="text-[1.0625rem] font-bold tracking-[-0.02em] text-ink">Noodle Studio</span>
            <BetaTag />
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 sm:px-6 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Hero: a glass card, the line the product stands on, and Pip and friends at the table */}
          <div className="ns-glass ns-glass-strong rounded-3xl p-6 sm:p-8 mb-8 grid grid-cols-1 md:grid-cols-[1.2fr_1fr] gap-6 items-center">
            <div className="min-w-0">
              <p className="ns-t-label mb-3">About Noodle Studio</p>
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight leading-[1.05]">Characters that stay the same, frame after frame.</h1>
              <p className="text-base text-ink-soft leading-relaxed mt-4 max-w-xl">
                Noodle Studio is a canvas for making pictures and films with AI models, built around one problem:
                keeping a character, a prop and a place the same across every shot. References travel with your
                project, scripts become storyboards, storyboards become shots, and everything you make is saved
                on your own disk as well as in the cloud.
              </p>
              <p className="ns-t-small mt-4">Pip, the fox in the pictures, is ours.</p>
            </div>
            <img src="/brand/joined.webp" alt="Pip, an owl chef and a kitten sharing one long noodle across a table on the pier" className="w-full aspect-[4/3] object-cover rounded-2xl" loading="lazy" decoding="async" />
          </div>

          {/* Mission Section */}
          <section className="ns-card mb-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="ns-mark ns-mark-lavender">
                <Zap aria-hidden="true" />
              </span>
              <h2 className="text-xl font-semibold text-ink">Our mission</h2>
            </div>
            <p className="text-ink-soft leading-relaxed">
              We believe creativity should be limitless. Our mission is to democratize AI-powered creative
              tools, making professional-grade image generation, video creation, and content writing
              accessible to everyone. Noodle Studio removes the barriers between imagination and
              creation, empowering artists, writers, designers, and dreamers to bring their visions to life.
            </p>
          </section>

          {/* Features Grid */}
          <section className="mb-6">
            <h2 className="text-xl font-semibold text-ink mb-4">What you can do</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {FEATURES.map(({ icon: Icon, tone, title, body }) => (
                <div key={title} className="ns-card p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <span data-tone={tone} className={tone ? 'ns-mark' : 'ns-mark ns-mark-lavender'}>
                      <Icon aria-hidden="true" />
                    </span>
                    <h3 className="font-semibold text-ink">{title}</h3>
                  </div>
                  <p className="text-ink-soft text-sm">{body}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Technology Section */}
          <section className="ns-card mb-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="ns-mark ns-mark-lavender">
                <Code aria-hidden="true" />
              </span>
              <h2 className="text-xl font-semibold text-ink">Built with modern tech</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-4 text-ink-soft">
              {TECH.map(({ title, items }) => (
                <div key={title}>
                  <h3 className="font-medium text-ink mb-2">{title}</h3>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    {items.map((item) => <li key={item}>{item}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </section>

          {/* Values Section */}
          <section className="ns-card mb-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="ns-mark ns-mark-lavender">
                <Heart aria-hidden="true" />
              </span>
              <h2 className="text-xl font-semibold text-ink">Our values</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {VALUES.map(({ icon: Icon, title, body }) => (
                <div key={title} className="text-center p-4">
                  <span className="ns-mark ns-mark-lavender grid mx-auto mb-3">
                    <Icon aria-hidden="true" />
                  </span>
                  <h3 className="font-semibold text-ink mb-2">{title}</h3>
                  <p className="text-ink-soft text-sm">{body}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Company Info */}
          <section className="ns-card mb-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="ns-mark ns-mark-lavender">
                <Globe aria-hidden="true" />
              </span>
              <h2 className="text-xl font-semibold text-ink">Noodle Studio</h2>
            </div>
            <div className="text-ink-soft space-y-3">
              <p>
                Noodle Studio is an independent software studio dedicated to building
                innovative AI-powered creative tools. We believe in the power of technology
                to unlock human potential and make professional creative tools accessible to all.
              </p>
              <div className="ns-well p-4">
                <p className="text-sm">
                  <strong className="text-ink">Something broke, or something is missing?</strong>{' '}
                  Say so on the <Link to="/bugs" className="ns-link">Known issues</Link> page. Every report is read, and once reviewed it shows there with a word on what was done.
                </p>
              </div>
            </div>
          </section>

          {/* Legal Links */}
          <div className="ns-card">
            <h2 className="text-lg font-semibold text-ink mb-4">Legal</h2>
            <div className="flex flex-wrap gap-3">
              <Link to="/terms" className="ns-btn ns-btn-soft">
                Terms of Service
                <ArrowRight size={16} aria-hidden="true" />
              </Link>
              <Link to="/privacy" className="ns-btn ns-btn-soft">
                Privacy Policy
                <ArrowRight size={16} aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-3 px-4 sm:px-6 border-t border-cream-300 bg-cream/70 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-3">
          <p className="text-ink-soft text-xs font-medium">© 2026 Noodle Studio</p>
          <nav aria-label="Footer" className="flex items-center gap-1">
            <Link to="/terms" className="ns-btn ns-btn-quiet ns-btn-xs">Terms</Link>
            <Link to="/privacy" className="ns-btn ns-btn-quiet ns-btn-xs">Privacy</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
};
